<template>
  <div class="w-full flex flex-col">
    <div
      v-if="label"
      class="Body-M text-[#878E8B] mb-2"
      :class="[labelClass, { 'text-CCCFCD': disableAction }]"
    >
      {{ label }}
    </div>
    <div
      class="date_picker_component flex items-center gap-[6px]"
      :class="{ 'w-fit': useTrigger, active: warningMessage }"
    >
      <VueDatePicker
        :auto-apply="useAutoApply"
        ref="VueDatePicker"
        :id="id"
        locale="th"
        :action-row="{
          showPreview: false,
          showSelect: false,
          showCancel: false,
          showNow: false,
        }"
        :month-picker="isMonthPicker"
        :year-picker="isYearPicker"
        :placeholder="placeholder"
        :min-date="timeOnly ? null : minDate"
        :max-date="timeOnly ? null : maxDate"
        :disabled="disableAction"
        :enable-time-picker="enableTime"
        :type="
          range ? 'date' : timeOnly ? 'time' : enableTime ? 'datetime' : 'date'
        "
        :range="range"
        :format="format || customFormat"
        :timePicker="enableTimeMode"
        @cleared="clearDate"
        :modelValue="internalValue"
        @update:modelValue="handleModelUpdate"
        calendar-type="buddhist"
        :teleport="teleport"
        :time-picker-inline="timePickerInline"
        :position="customPosition"
        :hide-input-icon="false"
        v-model="date"
        @time-picker-close="timePickerClose"
        @closed="popupPickerIsClosed"
        @open="onDatepickerPopupOpen"
        @internal-model-change="onInternalModelChange"
        :ui="ui"
      >
        <template #input-icon v-if="!useTrigger">
          <iconCalendar class="text-[#555055]" v-if="!date" />
          <CloseIcon
            v-else
            class="w-[24px] h-[24px] text-[#555055]"
            @click="
              (e) => {
                clearDate();
                e.stopPropagation();
              }
            "
          />
        </template>
        <template #clear-icon></template>
        <template #calendar-icon>
          <div
            class="w-[36px] h-[36px] flex items-center justify-center rounded-[6px] border-[1px] border-solid border-[#B7B2B5]"
          >
            <iconCalendar class="text-primary" />
          </div>
          <!-- <span>เลือกวันที่</span> -->
        </template>
        <template #trigger v-if="useTrigger">
          <div
            class="w-[45px] h-[45px] border border-CCCFCD border-solid bg-white rounded-full grid place-items-center cursor-pointer"
          >
            <iconCalendar />
          </div>
        </template>
        <template #year="{ value, year }">
          <span>{{ value ? value + 543 : year + 543 }}</span>
        </template>
        <template #year-overlay-value="{ text, value }">
          {{ value + 543 }}
        </template>
        <template
          v-if="!timeOnly && enableTimeMode == false"
          #action-row="{ internalModelValue, closePicker, selectDate }"
        >
          <div class="">
            <div
              v-if="internalModelValue && !timePickerInline"
              class="text-center content-center border-t border-secondary-light min-h-[42px]"
            >
              <span v-if="internalModelValue && !range" class="text-sm">
                <span v-if="monthPicker">
                  {{ $moment(internalModelValue).format("MMMM") }}
                  {{ $moment(internalModelValue).year() + 543 }}</span
                >
                <span v-else>{{ formatBuddhistDate(internalModelValue) }}</span>
              </span>
              <span v-else-if="internalModelValue && range" class="text-sm">
                {{
                  formatMDY(internalModelValue[0]) +
                  " - " +
                  formatMDY(internalModelValue[1])
                }}
              </span>
            </div>

            <div class="flex gap-2 p-[10px]">
              <button
                type="button"
                class="border border-[#B7B2B5] M text-primary-dark h-[42px] w-[115px] rounded-[6px]"
                @click="closePicker()"
              >
                ปิด
              </button>
              <button
                :disabled="
                  !internalModelValue ||
                  (Array.isArray(internalModelValue)
                    ? !internalModelValue[1]
                    : false)
                "
                type="button"
                class="bg-[#9B237B] text-[#FFF] M h-[42px] w-[115px] rounded-[6px] disabled:!bg-secondary-light disabled:!text-grey-light disabled:!cursor-not-allowed"
                @click="selectDate(internalModelValue)"
              >
                ตกลง
              </button>
            </div>
          </div>
        </template>
      </VueDatePicker>
    </div>
    <div v-if="warningMessage" class="Caption-M text-[#EB5959] mt-[8px]">
      {{ warningMessage }}
    </div>
  </div>
</template>

<script>
import iconCalendar from "@/assets/icons/iconCalendar.vue";
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import "@/assets/css/datepicker.scss";
import moment from "moment/moment";
import { nextTick } from "vue";

export default {
  setup(props) {
    const customPosition = props.position;
    return {
      customPosition,
    };
  },
  components: { iconCalendar, VueDatePicker },
  props: {
    id: {
      type: String,
      default: () => {
        return "date_component";
      },
    },
    labelClass: {
      type: String,
      default: "",
    },
    value: {
      type: [String, Array, Date],
      default: () => {
        return null;
      },
    },
    range: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: () => {
        return "";
      },
    },
    placeholder: {
      type: String,
      default: () => {
        return "เลือกวันเดือนปี";
      },
    },
    format: {
      type: String,
      default: "",
    },
    position: {
      type: String,
      default: () => {
        return "left";
      },
    },
    minDate: {
      type: [String, Date],
      default: () => {
        let date = new Date();
        date.setHours(23, 59, 59, 0);
        return date;
      },
    },
    maxDate: {
      type: [String, Date],
      default: () => null,
    },
    enableTime: {
      type: Boolean,
      default: () => {
        return false;
      },
    },
    disableAction: {
      type: Boolean,
      default: () => {
        return false;
      },
    },
    ui: {
      type: Object,
      default: () => ({}),
    },
    monthPicker: {
      type: Boolean,
      default: () => {
        return false;
      },
    },
    yearPicker: {
      type: Boolean,
      default: () => {
        return false;
      },
    },
    setFormatOnDate: {
      type: Boolean,
      default: () => {
        return false;
      },
    },
    useTrigger: {
      type: Boolean,
      default: () => {
        return false;
      },
    },
    showTypeFilter: {
      type: Boolean,
      default: () => {
        return false;
      },
    },
    warningMessage: {
      type: String,
      default: "",
    },
    timeOnly: {
      type: Boolean,
      default: false,
    },
    enableTimeMode: {
      type: Boolean,
      default: false,
    },
    teleport: {
      type: Boolean,
      default: false,
    },
    timePickerInline: {
      type: Boolean,
      default: false,
    },
    modelValue: [String, Date, Object],
    longMonth: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "update:modelClosed"],
  data() {
    return {
      // proxyValue: this.modelValue,
      internalModelValue: null,
      date: null,
      dateTime: this.value,
      isMonthPicker: this.monthPicker,
      isYearPicker: this.yearPicker,
      filterList: [
        {
          id: "0",
          name: "รายวัน",
        },
        {
          id: "1",
          name: "รายเดือน",
        },
        {
          id: "2",
          name: "รายปี",
        },
      ],
      filterId: "0",
    };
  },
  watch: {
    modelValue(val) {
      if (this.range && Array.isArray(val)) {
        console.log(val);
        // console.log(val.map((v) => (v instanceof Date ?  (v + 'not null') : 'is null')),
        this.date = val.map((v) => (v instanceof Date ? v : ""));
      } else {
        this.date = val;
      }
    },
    value(val) {
      console.log(val);
      if (!val) {
        this.date = null;
        this.dateTime = null;
        return;
      }
      if (this.isMonthPicker) {
        try {
          this.date = {
            month: val.getMonth(),
            year: val.getFullYear(),
          };
          this.filterId = "1";
        } catch (e) {
          this.date = null;
        }
      } else if (this.isYearPicker) {
        if (val) {
          try {
            this.date = val.getFullYear();
          } catch (e) {
            this.date = null;
          }
        } else {
          this.date = null;
        }
        this.filterId = "2";
      } else if (this.range && Array.isArray(val)) {
        this.date = [new Date(val[0]), new Date(val[1])];
      } else {
        try {
          if (typeof val === "string") {
            let parts = val.split("-");
            let formattedDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
            this.date = val ? new Date(formattedDate) : null;
          } else {
            this.date = val ? new Date(val) : null;
          }
        } catch (e) {
          this.date = null;
        }
        this.filterId = "0";
      }
    },
    isMonthPicker() {
      if (this.isMonthPicker) {
        this.date = {
          month: this.dateTime.getMonth(),
          year: this.dateTime.getFullYear(),
        };
        this.filterId = "1";
      }
    },
    isYearPicker() {
      if (this.isYearPicker) {
        this.date = this.dateTime.getFullYear();
        this.filterId = "2";
      }
    },
    filterId() {
      if (this.filterId == "0") {
        this.isMonthPicker = false;
        this.isYearPicker = false;
      } else if (this.filterId == "1") {
        this.isMonthPicker = true;
        this.isYearPicker = false;
      } else if (this.filterId == "2") {
        this.isMonthPicker = false;
        this.isYearPicker = true;
      }
      this.$emit("update:modelClosed", {
        date: this.dateTime,
        filterType: this.filterId,
      });
    },
  },

  mounted() {
    console.log("today->", this.$moment());
    // Initialize date based on modelValue
    if (this.timeOnly && this.modelValue) {
      if (
        typeof this.modelValue === "string" &&
        this.modelValue.includes(":")
      ) {
        const [hour, minute] = this.modelValue.split(":");
        const dateObj = new Date();
        dateObj.setHours(+hour, +minute, 0, 0);
        this.date = dateObj;
      }
    } else if (this.isMonthPicker && this.value) {
      this.date = {
        month: this.value.getMonth(),
        year: this.value.getFullYear(),
      };
      this.filterId = "1";
    } else if (this.isYearPicker) {
      this.date = this.value ? this.value.getFullYear() : null;
      this.filterId = "2";
    } else {
      this.date =
        this.modelValue || this.value
          ? new Date(this.modelValue || this.value)
          : null;
      this.filterId = "0";
    }

    if (this.timeOnly) {
      if (
        typeof this.modelValue === "string" &&
        this.modelValue.includes(":")
      ) {
        const [hour, minute] = this.modelValue.split(":");
        const dateObj = new Date();
        dateObj.setHours(+hour, +minute, 0, 0);
        this.date = dateObj;
      } else if (this.modelValue instanceof Date) {
        this.date = this.modelValue;
      } else {
        this.date = null;
      }

      // ✅ ดักการ scroll เวลาแบบเรียลไทม์
      this.$watch(
        () => this.$refs.VueDatePicker?.internalModelValue,
        (val) => {
          if (val instanceof Date) {
            const h = String(val.getHours()).padStart(2, "0");
            const m = String(val.getMinutes()).padStart(2, "0");
            const timeString = `${h}:${m}`;
            this.date = val;
            this.$emit("update:modelValue", timeString);
          }
        }
      );
    }
  },

  computed: {
    useAutoApply() {
      return this.timeOnly; // ใช้ auto-apply เฉพาะโหมด timeOnly
    },

    internalValue() {
      if (this.range && Array.isArray(this.modelValue)) {
        return this.modelValue.map((val) =>
          val instanceof Date ? val : new Date(val)
        );
      }
      if (
        this.timeOnly &&
        typeof this.modelValue === "string" &&
        this.modelValue.includes(":")
      ) {
        const [h, m] = this.modelValue.split(":");
        const d = new Date();
        d.setHours(+h, +m, 0, 0);
        return d;
      }

      if (this.modelValue instanceof Date) {
        return this.modelValue;
      }

      if (
        typeof this.modelValue === "string" &&
        this.modelValue.match(/^\d{4}-\d{2}-\d{2}/)
      ) {
        return new Date(this.modelValue); // fallback for ISO format
      }

      return this.date;
    },
  },
  methods: {
    onUpdate(val) {
      if (this.timeOnly && val instanceof Date) {
        const timeString = this.formatTime(val);
        this.date = val;
        this.$nextTick(() => {
          this.$emit("update:modelValue", timeString);
        });
      } else {
        this.date = val;
        this.$nextTick(() => {
          this.$emit("update:modelValue", val);
        });
      }
    },
    formatTime(dateObj) {
      if (!(dateObj instanceof Date)) return dateObj;
      const h = String(dateObj.getHours()).padStart(2, "0");
      const m = String(dateObj.getMinutes()).padStart(2, "0");
      return `${h}:${m}`;
    },
    clearDate() {
      this.date = null;
      this.dateTime = null;
      this.$emit("update:modelValue", null);

      this.$nextTick(() => {
        this.date = null;
      });
    },
    timePickerClose() {
      console.log("tricker close");
    },
    popupPickerIsClosed() {
      this.$emit("update:modelClosed", {
        date: this.dateTime,
        filterType: this.filterId,
      });
    },
    formatMDY(event) {
      let dates;
      if (event) {
        if (this.setFormatOnDate) {
          dates = this.$moment(this.value);
        } else {
          if (typeof event === "string" && /^\d{2}-\d{2}-\d{4}$/.test(event)) {
            dates = this.$moment(event, "DD-MM-YYYY", true);
          } else {
            dates = this.$moment(event);
          }
        }
      }

      if (dates && !dates.isValid()) {
        return "";
      }

      const buddhistYear = dates ? dates.year() + 543 : "";

      let formattedDate;
      if (dates) {
        if (this.isYearPicker) {
          formattedDate = buddhistYear.toString();
        } else if (this.isMonthPicker) {
          formattedDate = `${
            this.longMonth ? dates.format("MMMM") : dates.format("MMM")
          } ${buddhistYear}`;
        } else if (this.range) {
          formattedDate = `${dates.format("D MMM")} ${buddhistYear}`;
        } else {
          formattedDate = this.format
            ? dates
                .format(this.format)
                .replace(dates.format("YYYY"), buddhistYear)
            : this.enableTime
            ? `${dates.format("D MMM")} ${buddhistYear}, ${dates.format(
                "HH:mm"
              )}`
            : `${dates.format("DD/MM/")}${buddhistYear}`;
        }
        return formattedDate;
      }
      return "";
    },
    closeDatepicker() {
      this.$refs.VueDatePicker.closeMenu();
    },
    formatBuddhistDate(dateObj) {
      if (!(dateObj instanceof Date)) return "";
      const day = dateObj.getDate();
      const month = dateObj.toLocaleString("th-TH", { month: "long" }); // e.g. "มกราคม"
      const year = dateObj.getFullYear() + 543;
      return `${day} ${month} ${year}`;
    },
    // formatFullMonth(monthIndex) {
    //   const monthNames = [
    //     "มกราคม",
    //     "กุมภาพันธ์",
    //     "มีนาคม",
    //     "เมษายน",
    //     "พฤษภาคม",
    //     "มิถุนายน",
    //     "กรกฎาคม",
    //     "สิงหาคม",
    //     "กันยายน",
    //     "ตุลาคม",
    //     "พฤศจิกายน",
    //     "ธันวาคม",
    //   ];
    //   return monthNames[monthIndex] || "";
    // },
    customFormat(date) {
      if (this.range && Array.isArray(date) && date.length === 2) {
        return this.formatMDY(date[0]) + " - " + this.formatMDY(date[1]);
      }
      if (this.timeOnly) {
        return this.formatTime(date); // ส่ง HH:mm
      } else {
        return this.formatMDY(date);
      }
    },
    handleModelUpdate(val) {
      if (this.range && Array.isArray(val)) {
        this.date = val;
        this.$emit("update:modelValue", val);
      } else if (this.timeOnly && val instanceof Date) {
        const h = String(val.getHours()).padStart(2, "0");
        const m = String(val.getMinutes()).padStart(2, "0");
        const timeString = `${h}:${m}`;
        this.date = val;
        this.dateTime = timeString;
        this.$emit("update:modelValue", timeString);
      } else {
        this.date = val;
        this.dateTime = val;
        this.$emit("update:modelValue", val);
      }
    },

    async onDatepickerPopupOpen() {
      await nextTick();
      if (this.timePickerInline) {
        const tpic = document.querySelectorAll(
          ".dp__time_picker_inline_container"
        );

        tpic[0].style.alignItems = "center";
        tpic[0].insertAdjacentHTML("beforebegin", `<hr class="mb-1.5"/>`);
        tpic[0].insertAdjacentHTML(
          "afterbegin",
          `<div class="w-1/2 text-center text-sm date-display">${
            this.date
              ? moment(this.date).format("DD/MM/") +
                (parseInt(moment(this.date).format("YYYY")) +
                  543) +
                ", " +
                moment(this.date).format("HH:mm")
              : "-"
          }</div>`
        );
      }
    },
    async onInternalModelChange(val) {
      this.internalModelValue = val;
      if (this.timePickerInline) {
        await nextTick();
        const dateDisplay = document.querySelector(".date-display");
        if (dateDisplay) {
          dateDisplay.innerHTML = val
            ? moment(val).format("DD/MM/") +
              (parseInt(moment(val).format("YYYY")) + 543) +
              ", " +
              moment(val).format("HH:mm")
            : "-";
        }
      }
    },
  },
};
</script>

<style scoped lang="scss"></style>
